import React from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import { CartContext } from '../cart/CartHandler';
import { isCabinetItem } from '../cart/CartItem';
import QuoteMarkdown from '../common/QuoteMarkdown';
import QuoteDescriptionEditor from './QuoteDescriptionEditor';

/**
 * Árajánlat tab (admin only): saves the current cart as a quote cart (uc_quote_properties row) with a
 * fixed gross total and access options, returns a `designer/q/<code>` link (+ legacy `designer?quote=<token>`). The server re-checks
 * the employee session and validates every field.
 */

type GuestMode = 0 | 1 | 2;

const GUEST_HELP: Record<GuestMode, string> = {
    0: 'Csak a megadott e-mail címmel bejelentkezett vásárló nyithatja meg.',
    1: 'Bárki megnyithatja ár és rendelés nélkül. Árat és rendelést csak a címzett lát.',
    2: 'Bárki megnyithatja és megrendelheti, mintha nem lenne korlátozás.',
};

const DESCRIPTION_MAX = 10000;
const IMAGE_MAX_BYTES = 8 * 1024 * 1024;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// Local YYYY-MM-DD (toISOString would shift to UTC)
const localToday = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const QuoteTab: React.FC = () => {
    const cart = React.useContext(CartContext);
    const [total, setTotal] = React.useState<string>('');
    const [saving, setSaving] = React.useState(false);
    const [url, setUrl] = React.useState<string>('');
    const [urlLong, setUrlLong] = React.useState<string>('');
    const [error, setError] = React.useState<string | null>(null);
    const [copied, setCopied] = React.useState<'short' | 'long' | null>(null);
    const [email, setEmail] = React.useState<string>('');
    const [validUntil, setValidUntil] = React.useState<string>('');
    const [linkExpiresAt, setLinkExpiresAt] = React.useState<string>('');
    const [guestMode, setGuestMode] = React.useState<GuestMode>(0);
    const [updated, setUpdated] = React.useState(false);
    const [deleting, setDeleting] = React.useState(false);
    const [info, setInfo] = React.useState<string | null>(null);
    const [description, setDescription] = React.useState<string>('');
    const [preview, setPreview] = React.useState(false);
    const [priceInfo, setPriceInfo] = React.useState<string>('');
    const [priceInfoPreview, setPriceInfoPreview] = React.useState(false);
    // Saved image URL; new file (not uploaded yet) + its object URL; removal flag
    const [imageUrl, setImageUrl] = React.useState<string>('');
    const [imageFile, setImageFile] = React.useState<File | null>(null);
    const [imageObjectUrl, setImageObjectUrl] = React.useState<string>('');
    const [removeImage, setRemoveImage] = React.useState(false);
    const [imageError, setImageError] = React.useState<string | null>(null);
    const fileInput = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => () => { if (imageObjectUrl) URL.revokeObjectURL(imageObjectUrl); }, [imageObjectUrl]);

    // Prefill from the quote already saved from this cart; re-save keeps its link
    React.useEffect(() => {
        let active = true;
        cart.getSavedQuote().then((saved) => {
            if (!active || !saved) return;
            setTotal(String(Math.round(saved.total)));
            setEmail(saved.email);
            setValidUntil(saved.validUntil);
            setLinkExpiresAt(saved.linkExpiresAt);
            setGuestMode(saved.email ? saved.guestMode : 0);
            setUrl(saved.url);
            setUrlLong(saved.urlLong ?? '');
            setDescription(saved.description ?? '');
            setPriceInfo(saved.priceInfo ?? '');
            setImageUrl(saved.imageUrl ?? '');
        });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = Number(total.replace(',', '.'));
    // Admin-edited ucon/style: the current design alone is quoted, the cart is replaced
    const fromDesign = cart.adminDesign;
    const hasItems = fromDesign ? cart.cabinetResult != null : cart.cartItems.some(isCabinetItem);
    const today = localToday();
    const hasEmail = email.trim() !== '';
    const emailValid = !hasEmail || /^[^\s@]+@[^\s@]+$/.test(email.trim());
    // Past dates may be saved (re-save of an expired quote); only flagged
    const datePast = (d: string) => d !== '' && d < today;
    const pastHelper = { sx: { color: 'warning.main', fontWeight: 'bold' } };
    const valid = total.trim() !== '' && Number.isFinite(value) && value > 0 && emailValid
        && description.length <= DESCRIPTION_MAX && priceInfo.length <= DESCRIPTION_MAX;
    const shownImage = imageObjectUrl || (removeImage ? '' : imageUrl);

    const handleImagePick = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!IMAGE_TYPES.includes(file.type)) {
            setImageError('Csak JPEG, PNG vagy WEBP kép tölthető fel.');
            return;
        }
        if (file.size > IMAGE_MAX_BYTES) {
            setImageError('A kép legfeljebb 8 MB lehet.');
            return;
        }
        setImageError(null);
        setImageFile(file);
        setImageObjectUrl(URL.createObjectURL(file));
        setRemoveImage(false);
    };

    const handleImageRemove = () => {
        setImageFile(null);
        setImageObjectUrl('');
        setRemoveImage(imageUrl !== '');
        setImageError(null);
    };

    // Empty -> filled e-mail: restriction starts as "Nem elérhető"
    const handleEmailChange = (v: string) => {
        if (!hasEmail && v.trim() !== '') setGuestMode(0);
        setEmail(v);
    };

    const handleSave = async () => {
        if (fromDesign && cart.cartItems.some(isCabinetItem)
            && !window.confirm('A kosár tartalma törlődik, csak a jelenlegi terv kerül az árajánlatba. Folytatja?')) {
            return;
        }
        setSaving(true);
        setError(null);
        setCopied(null);
        setUpdated(false);
        setInfo(null);
        const input = {
            total: value, email, validUntil, linkExpiresAt, guestMode: (hasEmail ? guestMode : 2) as GuestMode,
            description, priceInfo, image: imageFile, removeImage,
        };
        const result = fromDesign ? await cart.quoteCurrentDesign(input) : await cart.createQuote(input);
        setSaving(false);
        if (result.error || !result.url) {
            setError(result.error ?? 'Az árajánlat mentése sikertelen.');
            return;
        }
        setUrl(result.url);
        setUrlLong(result.urlLong ?? '');
        setUpdated(!!result.updated);
        // Saved image now served from the server
        const saved = await cart.getSavedQuote();
        setImageUrl(saved?.imageUrl ?? '');
        setImageFile(null);
        setImageObjectUrl('');
        setRemoveImage(false);
    };

    const handleDelete = async () => {
        if (!window.confirm('Biztosan törlöd az árajánlatot? A link megszűnik, a kosár normál kosár lesz.')) return;
        setDeleting(true);
        setError(null);
        setInfo(null);
        const result = await cart.deleteQuote();
        setDeleting(false);
        if (result.error) {
            setError(result.error);
            return;
        }
        setUrl('');
        setUrlLong('');
        setUpdated(false);
        setCopied(null);
        setTotal('');
        setEmail('');
        setValidUntil('');
        setLinkExpiresAt('');
        setGuestMode(0);
        setDescription('');
        setPriceInfo('');
        setImageUrl('');
        setImageFile(null);
        setImageObjectUrl('');
        setRemoveImage(false);
        setInfo(result.deleted > 0 ? 'Árajánlat törölve.' : 'Nem volt törölhető árajánlat.');
    };

    const handleCopy = async (which: 'short' | 'long') => {
        try {
            await navigator.clipboard.writeText(which === 'short' ? url : urlLong);
            setCopied(which);
        } catch {
            setCopied(null);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
                {fromDesign
                    ? 'A jelenlegi terv (UCon + stílus szkript) egyetlen árajánlat-termékként mentődik, a megadott bruttó végösszeggel. A kosár többi tartalma törlődik.'
                    : 'A jelenlegi kosár árajánlatként mentődik, a megadott bruttó végösszeggel.'}
            </Typography>

            <TextField
                label="Árajánlat végösszeg"
                type="number"
                size="small"
                value={total}
                onChange={(e) => setTotal(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                InputProps={{ endAdornment: <InputAdornment position="end">Ft</InputAdornment> }}
                disabled={saving}
            />

            <Box component="fieldset" sx={{ m: 0, px: 1.5, pt: 0.5, pb: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography component="legend" variant="caption" sx={{ px: 0.5, color: 'text.secondary' }}>
                    E-mail korlátozás
                </Typography>

                <TextField
                    label="E-mail cím (opcionális)"
                    type="email"
                    size="small"
                    value={email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    error={!emailValid}
                    helperText="Üresen bárki megnyithatja és megrendelheti."
                    disabled={saving}
                />

                {hasEmail && (
                    <TextField
                        select
                        label="Vendég felhasználó"
                        size="small"
                        value={guestMode}
                        onChange={(e) => setGuestMode(Number(e.target.value) as GuestMode)}
                        helperText={GUEST_HELP[guestMode]}
                        disabled={saving}
                    >
                        <MenuItem value={0}>Nem elérhető</MenuItem>
                        <MenuItem value={1}>Elérhető ár nélkül</MenuItem>
                        <MenuItem value={2}>Elérhető és rendelhető</MenuItem>
                    </TextField>
                )}
            </Box>

            <TextField
                label="Árajánlat érvényessége (opcionális)"
                type="date"
                size="small"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                inputProps={{ min: today }}
                InputLabelProps={{ shrink: true }}
                helperText={datePast(validUntil)
                    ? 'A dátum elmúlt: az ár lejártként jelenik meg, nem rendelhető.'
                    : 'Az ár e nap végéig érvényes.'}
                FormHelperTextProps={datePast(validUntil) ? pastHelper : undefined}
                disabled={saving}
            />

            <TextField
                label="Link lejárata (opcionális)"
                type="date"
                size="small"
                value={linkExpiresAt}
                onChange={(e) => setLinkExpiresAt(e.target.value)}
                inputProps={{ min: today }}
                InputLabelProps={{ shrink: true }}
                helperText={datePast(linkExpiresAt)
                    ? 'A dátum elmúlt: a link nem nyitható meg.'
                    : 'E nap után a link nem nyitható meg. Üresen nem jár le.'}
                FormHelperTextProps={datePast(linkExpiresAt) ? pastHelper : undefined}
                disabled={saving}
            />

            <Box component="fieldset" sx={{ m: 0, px: 1.5, pt: 0.5, pb: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography component="legend" variant="caption" sx={{ px: 0.5, color: 'text.secondary' }}>
                    Előnézeti kép
                </Typography>
                {shownImage && (
                    <Box component="img" src={shownImage} alt="Előnézeti kép"
                        sx={{ display: 'block', width: 240, maxWidth: '100%', aspectRatio: '1200 / 630', objectFit: 'cover', bgcolor: 'grey.100', borderRadius: 1 }} />
                )}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="outlined" startIcon={<AddPhotoAlternateIcon />} disabled={saving}
                        onClick={() => fileInput.current?.click()}>
                        {shownImage ? 'Kép cseréje' : 'Kép feltöltése'}
                    </Button>
                    {shownImage && (
                        <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} disabled={saving} onClick={handleImageRemove}>
                            Kép törlése
                        </Button>
                    )}
                </Box>
                <input ref={fileInput} type="file" accept={IMAGE_TYPES.join(',')} hidden onChange={handleImagePick} />
                <Typography variant="caption" color={imageError ? 'error' : 'text.secondary'}>
                    {imageError ?? (imageFile || removeImage
                        ? 'Mentéskor kerül tárolásra.'
                        : 'Középre vágva 1200×630 px-re, a link megosztásakor jelenik meg. JPEG, PNG, WEBP, max. 8 MB.')}
                </Typography>
            </Box>

            <Box component="fieldset" sx={{ m: 0, px: 1.5, pt: 0.5, pb: 1, border: 1, borderColor: 'divider', borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography component="legend" variant="caption" sx={{ px: 0.5, color: 'text.secondary' }}>
                    Leírás
                </Typography>
                <FormControlLabel
                    control={<Switch size="small" checked={preview} onChange={(e) => setPreview(e.target.checked)} />}
                    label={<Typography variant="caption" sx={{ color: 'text.secondary' }}>Előnézet</Typography>}
                />
                {preview ? (
                    <Box sx={{ border: 1, borderColor: 'grey.400', borderRadius: 1, p: 1.5, minHeight: 140, bgcolor: '#fff' }}>
                        {description
                            ? <QuoteMarkdown markdown={description} />
                            : <Typography variant="caption" color="text.secondary">Nincs leírás.</Typography>}
                    </Box>
                ) : (
                    <QuoteDescriptionEditor value={description} onChange={setDescription} disabled={saving} />
                )}
                <Typography variant="caption" color="text.secondary">
                    A vásárló az árajánlat megnyitásakor látja.
                </Typography>
            </Box>

            <Box component="fieldset" sx={{ m: 0, px: 1.5, pt: 0.5, pb: 1, border: 1, borderColor: 'divider', borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography component="legend" variant="caption" sx={{ px: 0.5, color: 'text.secondary' }}>
                    Mit tartalmaz az ár?
                </Typography>
                <FormControlLabel
                    control={<Switch size="small" checked={priceInfoPreview} onChange={(e) => setPriceInfoPreview(e.target.checked)} />}
                    label={<Typography variant="caption" sx={{ color: 'text.secondary' }}>Előnézet</Typography>}
                />
                {priceInfoPreview ? (
                    <Box sx={{ border: 1, borderColor: 'grey.400', borderRadius: 1, p: 1.5, minHeight: 140, bgcolor: '#fff' }}>
                        {priceInfo
                            ? <QuoteMarkdown markdown={priceInfo} />
                            : <Typography variant="caption" color="text.secondary">Nincs szöveg.</Typography>}
                    </Box>
                ) : (
                    <QuoteDescriptionEditor value={priceInfo} onChange={setPriceInfo} disabled={saving} />
                )}
                <Typography variant="caption" color="text.secondary">
                    A vásárló az ár melletti ⓘ ikonra kattintva látja. Üresen az ikon nem jelenik meg.
                </Typography>
            </Box>

            <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !valid || !hasItems}
                startIcon={saving ? <CircularProgress size={16} /> : undefined}
            >
                Mentés
            </Button>

            {!hasItems && (
                <Typography variant="caption" color="text.secondary">
                    {fromDesign ? 'A Unity még nem adott eredményt a tervre.' : 'A kosár üres.'}
                </Typography>
            )}

            {error && (
                <Typography variant="caption" color="error">{error}</Typography>
            )}

            {url && (
                <TextField
                    label="Megosztható link"
                    size="small"
                    value={url}
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton aria-label="másolás" onClick={() => handleCopy('short')} edge="end">
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    helperText={copied === 'short' ? 'Vágólapra másolva.' : (updated ? 'Árajánlat frissítve, a link változatlan.' : undefined)}
                    onFocus={(e) => e.target.select()}
                />
            )}

            {urlLong && (
                <TextField
                    label="Hosszú link (régi formátum)"
                    size="small"
                    value={urlLong}
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton aria-label="másolás" onClick={() => handleCopy('long')} edge="end">
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    helperText={copied === 'long' ? 'Vágólapra másolva.' : undefined}
                    onFocus={(e) => e.target.select()}
                />
            )}

            {info && (
                <Typography variant="caption" color="text.secondary">{info}</Typography>
            )}

            {(url || cart.isQuote) && (
                <Button
                    variant="contained"
                    color="error"
                    sx={{
                        bgcolor: '#d32f2f !important',
                        color: '#fff !important',
                        '&:hover': { bgcolor: '#b71c1c !important' },
                        '&.Mui-disabled': { bgcolor: '#ef9a9a !important', color: '#fff !important' },
                    }}
                    onClick={handleDelete}
                    disabled={saving || deleting}
                    startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    Árajánlat törlése
                </Button>
            )}
        </Box>
    );
};

export default QuoteTab;
